import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Checkbox,
  Chip,
  Input,
  Select,
  SelectItem,
  Pagination,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { getAllExporters } from 'common/src/api/exporters';
import type { Exporter } from 'common/src/utils/types';
import {
  inboxRequest,
  syncInbox,
  type InboxAddress,
  type InboxAddressList,
} from 'common/src/api/settlement-inbox';
import { useTranslation } from 'common/src/hooks/useTranslation';
import Header from '../../../components/layouts/Header';
import {
  Alert,
  EmptyState,
  LoadingState,
  PageLayout,
} from '../../../components/ui';
import InboxSenderApprovals from '../../../components/settlements/inbox-sender-approvals';

export default function SuperadminInboxAddresses() {
  const { t } = useTranslation('inbox');
  const [exporters, setExporters] = useState<Exporter[]>([]);
  const [exportersReady, setExportersReady] = useState(false);
  const [exportersLoading, setExportersLoading] = useState(true);
  const [exportersError, setExportersError] = useState(false);
  const [exporterId, setExporterId] = useState('');
  const [localPart, setLocalPart] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<InboxAddressList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configuringAddress, setConfiguringAddress] = useState<string | null>(
    null
  );
  const [toggleTarget, setToggleTarget] = useState<InboxAddress | null>(null);
  const [cleanupTarget, setCleanupTarget] = useState<InboxAddress | null>(null);
  const [reactivateConfirmed, setReactivateConfirmed] = useState(false);
  const requestId = useRef(0);
  const exporterRequestId = useRef(0);
  const normalizedPart = localPart.trim().toLowerCase();
  const addressPrefix = data?.addressPrefix || '';
  const fullPart = `${addressPrefix}${normalizedPart}`;
  const validPart =
    /^[a-z0-9][a-z0-9._-]{0,63}$/.test(normalizedPart) && fullPart.length <= 64;
  const address = `${fullPart}@fruitmarkets.com`;
  const exporterName = (id: number) => {
    const exporter = exporters.find((item) => item.id === id);
    return `${exporter?.name || exporter?.excel_name || t('Exportadora')} · ID ${id}`;
  };

  const load = useCallback(async () => {
    const sequence = ++requestId.current;
    setLoading(true);
    setError(false);
    try {
      const result = await inboxRequest<InboxAddressList>('admin-addresses', {
        page: page - 1,
        ...(filter === 'all' ? {} : { exporterId: Number(filter) }),
      });
      if (sequence !== requestId.current) return;
      setData(result);
    } catch {
      if (sequence === requestId.current) setError(true);
    } finally {
      if (sequence === requestId.current) setLoading(false);
    }
  }, [filter, page]);

  const loadExporters = useCallback(async () => {
    const sequence = ++exporterRequestId.current;
    setExportersLoading(true);
    setExportersError(false);
    try {
      const companies = await getAllExporters();
      if (sequence !== exporterRequestId.current) return;
      setExporters(companies);
      setExportersReady(true);
    } catch {
      if (sequence === exporterRequestId.current) setExportersError(true);
    } finally {
      if (sequence === exporterRequestId.current) setExportersLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadExporters();
    return () => {
      exporterRequestId.current++;
    };
  }, [loadExporters]);

  useEffect(() => {
    void load();
    return () => {
      requestId.current++;
    };
  }, [load]);

  const reportError = (failure: unknown) => {
    const code = failure instanceof Error ? failure.message : '';
    if (code === 'GOOGLE_GROUP_PROPAGATION_PENDING') {
      toast.warning(
        t(
          'Google aún está preparando el grupo. Espera un momento y pulsa Reintentar configuración; no necesitas crear otra dirección.'
        )
      );
      return;
    }
    if (code === 'INBOX_ADDRESS_OUTSIDE_ENVIRONMENT') {
      toast.error(
        t(
          'La dirección no coincide con el prefijo de este ambiente. Actualiza la página e intenta nuevamente.'
        )
      );
      return;
    }
    if (code === 'ADDRESS_CLEANUP_NOT_ALLOWED') {
      toast.error(
        t(
          'No se puede limpiar: la configuración está en curso o la dirección tiene un grupo o archivos asociados.'
        )
      );
      return;
    }
    if (code === 'ADDRESS_SETUP_DISMISSED') {
      toast.error(
        t(
          'Esta dirección tiene una configuración archivada y sigue reservada para su exportadora.'
        )
      );
      return;
    }
    toast.error(
      code === 'ADDRESS_IN_USE'
        ? t(
            'Esta dirección ya está registrada. No se puede transferir a otra exportadora.'
          )
        : code === 'ROUTING_CONFIRMATION_REQUIRED'
          ? t('Confirma primero la configuración en Google Workspace.')
          : code === 'EXPORTER_NOT_FOUND'
            ? t('La exportadora ya no existe. Actualiza la lista.')
            : t('No se pudo completar la acción. Intenta nuevamente.')
    );
  };

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t('Copiado'));
    } catch {
      toast.error(
        t('No se pudo copiar. Selecciona y copia la dirección manualmente.')
      );
    }
  };

  const register = async () => {
    if (!exporterId || !validPart || !data?.adminConfigured || saving) return;
    setSaving(true);
    setConfiguringAddress(address);
    try {
      await inboxRequest('create-group', {
        exporterId: Number(exporterId),
        address,
      });
      toast.success(t('Grupo configurado. Falta verificar la entrega.'));
      setLocalPart('');
      setFilter(exporterId);
      setPage(1);
    } catch (failure) {
      reportError(failure);
    } finally {
      // Creation may have succeeded partially. Show the persisted record even
      // if Google exhausted its propagation retries, so it can be resumed.
      await load();
      setConfiguringAddress(null);
      setSaving(false);
    }
  };

  const setupAction = async (item: InboxAddress, verify: boolean) => {
    if (saving) return;
    setSaving(true);
    if (!verify) setConfiguringAddress(item.address);
    try {
      if (verify) {
        const result = await syncInbox();
        toast.info(
          t(
            result.failed
              ? 'La sincronización tuvo errores. Revisa la configuración.'
              : result.more
                ? 'Hay más correos por revisar. Vuelve a verificar.'
                : 'Sincronización terminada. Revisa el estado de la dirección.'
          )
        );
      } else
        await inboxRequest('create-group', {
          address: item.address,
          exporterId: item.exporter_id,
        });
    } catch (failure) {
      reportError(failure);
    } finally {
      await load();
      setConfiguringAddress(null);
      setSaving(false);
    }
  };

  const toggle = async () => {
    if (!toggleTarget || saving) return;
    setSaving(true);
    try {
      await inboxRequest('set-address-active', {
        exporterId: toggleTarget.exporter_id,
        address: toggleTarget.address,
        active: !toggleTarget.active,
        routingConfirmed: reactivateConfirmed,
      });
      toast.success(t('Estado actualizado'));
      setToggleTarget(null);
      await load();
    } catch (failure) {
      reportError(failure);
    } finally {
      setSaving(false);
    }
  };

  const cleanup = async () => {
    if (!cleanupTarget || saving) return;
    setSaving(true);
    try {
      await inboxRequest('dismiss-address-setup', {
        exporterId: cleanupTarget.exporter_id,
        address: cleanupTarget.address,
        confirmed: true,
      });
      toast.success(t('Configuración archivada'));
      setCleanupTarget(null);
      if (data?.addresses.length === 1 && page > 1) setPage(page - 1);
      else await load();
    } catch (failure) {
      reportError(failure);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout className='flex flex-col gap-5'>
      <Header
        title={t('Correos de liquidaciones')}
        description={t(
          'Administra las direcciones @fruitmarkets.com de todas las exportadoras, sin crear usuarios de Google.'
        )}
        actions={
          <Button
            variant='flat'
            onPress={() => {
              void load();
              void loadExporters();
            }}
            isDisabled={loading || exportersLoading || saving}
          >
            {t('Actualizar')}
          </Button>
        }
      />

      <Card shadow='sm'>
        <CardBody className='gap-5 p-5'>
          <h3 className='text-lg font-semibold'>
            {t('Crear dirección en Google')}
          </h3>
          {data && !data.adminConfigured && (
            <Alert variant='warning'>
              <div role='status' className='flex flex-col gap-2'>
                <p className='font-semibold'>
                  {t('Falta conectar Google Admin')}
                </p>
                <p>
                  {t(
                    'Configura GOOGLE_ADMIN_CLIENT_ID, GOOGLE_ADMIN_CLIENT_SECRET y GOOGLE_ADMIN_REFRESH_TOKEN en el backend. La conexión de Gmail solo permite leer correos.'
                  )}
                </p>
                <p>
                  {t(
                    'Permisos: admin.directory.group, admin.directory.domain.readonly y apps.groups.settings. Habilita Admin SDK y Groups Settings API.'
                  )}
                </p>
              </div>
            </Alert>
          )}
          {exportersError && (
            <Alert variant='danger'>
              <div
                role='alert'
                className='flex flex-wrap items-center justify-between gap-2'
              >
                {t('No se pudieron cargar las exportadoras.')}
                <Button
                  size='sm'
                  variant='light'
                  onPress={() => void loadExporters()}
                >
                  {t('Reintentar')}
                </Button>
              </div>
            </Alert>
          )}
          <div className='grid gap-4 md:grid-cols-2'>
            <Select
              label={t('Exportadora')}
              placeholder={t('Selecciona una exportadora')}
              selectedKeys={exporterId ? [exporterId] : []}
              isDisabled={!exportersReady || saving}
              isLoading={exportersLoading}
              description={
                exportersReady && !exporters.length
                  ? t('No hay exportadoras disponibles.')
                  : undefined
              }
              onSelectionChange={(keys) => {
                setExporterId(String(Array.from(keys)[0] || ''));
              }}
            >
              {exporters.map((item) => (
                <SelectItem
                  key={String(item.id)}
                  textValue={exporterName(item.id)}
                >
                  {exporterName(item.id)}
                </SelectItem>
              ))}
            </Select>
            <Input
              label={t('Nombre de la dirección')}
              placeholder='exportername'
              value={localPart}
              startContent={
                addressPrefix ? (
                  <span className='text-small text-default-500 whitespace-nowrap'>
                    {addressPrefix}
                  </span>
                ) : undefined
              }
              description={
                addressPrefix
                  ? t(
                      'El prefijo de este ambiente se agrega automáticamente. Escribe solo el nombre de la exportadora.'
                    )
                  : undefined
              }
              endContent={
                <span className='text-small text-default-500 whitespace-nowrap'>
                  @fruitmarkets.com
                </span>
              }
              isDisabled={saving}
              maxLength={64 - addressPrefix.length}
              autoCapitalize='none'
              autoCorrect='off'
              isInvalid={localPart.length > 0 && !validPart}
              errorMessage={t(
                'Usa hasta 64 letras sin acentos, números, puntos, guiones o guiones bajos; comienza con una letra o número.'
              )}
              onValueChange={(value) => {
                setLocalPart(value);
              }}
            />
          </div>

          <Alert>
            <div className='flex flex-col gap-3'>
              <h4 className='font-semibold'>
                {t('Creación automática, sin catch-all')}
              </h4>
              <p>
                {t(
                  'Crearemos un grupo privado de distribución y conectaremos intake@cherrymarket.cl. No se crea un usuario ni se modifica el correo del equipo.'
                )}
              </p>
              <ol className='list-decimal pl-5 space-y-2'>
                <li>
                  {t(
                    'El nombre se reserva en fruitmarkets.com y cherrymarket.cl por la configuración de dominio alias.'
                  )}
                </li>
                <li>
                  {t(
                    'Los correos externos pueden llegar al grupo, pero sus remitentes no pueden ver miembros ni conversaciones.'
                  )}
                </li>
                <li>
                  {t(
                    'Después de crear el grupo, envía el correo de verificación indicado abajo. La dirección no se activa hasta comprobar su entrega.'
                  )}
                </li>
              </ol>
              {validPart && (
                <div className='flex flex-wrap items-center gap-2'>
                  <code className='break-all'>
                    {address} → intake@cherrymarket.cl
                  </code>
                  <Button
                    size='sm'
                    variant='flat'
                    onPress={() => void copy(address)}
                  >
                    {t('Copiar dirección')}
                  </Button>
                </div>
              )}
            </div>
          </Alert>
          <div className='flex flex-wrap gap-3 items-center'>
            <Button
              color='primary'
              isLoading={saving && !toggleTarget && !cleanupTarget}
              isDisabled={
                !exportersReady ||
                !exporterId ||
                !validPart ||
                !data?.adminConfigured ||
                saving
              }
              onPress={() => void register()}
            >
              {t(
                configuringAddress
                  ? 'Configurando en Google…'
                  : 'Crear dirección en Google'
              )}
            </Button>
            {exporterId && validPart && (
              <p className='text-sm text-default-500 break-all'>
                {address} → {exporterName(Number(exporterId))}
              </p>
            )}
          </div>
          {configuringAddress && (
            <p role='status' className='text-sm text-default-500'>
              {t(
                'Google puede tardar alrededor de un minuto en preparar el grupo. Reintentamos automáticamente; mantén esta página abierta.'
              )}
            </p>
          )}
        </CardBody>
      </Card>

      <section className='space-y-4' aria-label={t('Direcciones registradas')}>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <h3 className='text-lg font-semibold'>
            {t('Direcciones registradas')}
          </h3>
          <Select
            className='max-w-xs'
            label={t('Filtrar por exportadora')}
            selectedKeys={[filter]}
            isDisabled={saving}
            onSelectionChange={(keys) => {
              setFilter(String(Array.from(keys)[0] || 'all'));
              setPage(1);
            }}
          >
            {[
              <SelectItem key='all'>{t('Todas las exportadoras')}</SelectItem>,
              ...exporters.map((item) => (
                <SelectItem
                  key={String(item.id)}
                  textValue={exporterName(item.id)}
                >
                  {exporterName(item.id)}
                </SelectItem>
              )),
            ]}
          </Select>
        </div>
        <p className='text-sm text-default-500'>
          {t(
            'Las direcciones no se pueden transferir entre exportadoras. Pausar la recepción no borra archivos ni cambia las rutas de Google.'
          )}
        </p>
        {loading ? (
          <LoadingState label={t('Cargando direcciones')} />
        ) : error ? (
          <Alert variant='danger'>
            <div
              role='alert'
              className='flex flex-wrap items-center justify-between gap-2'
            >
              {t(
                'No se pudieron cargar las direcciones. Verifica que la migración y la función settlement-inbox estén disponibles.'
              )}
              <Button size='sm' variant='flat' onPress={() => void load()}>
                {t('Actualizar')}
              </Button>
            </div>
          </Alert>
        ) : data?.addresses.length ? (
          <div className='space-y-3'>
            {data.addresses.map((item) => (
              <Card key={item.address} shadow='sm'>
                <CardBody className='flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between p-4'>
                  <div className='min-w-0'>
                    <p className='font-medium break-all'>{item.address}</p>
                    <p className='text-sm text-default-500'>
                      {exporterName(item.exporter_id)}
                    </p>
                    {item.setup_error &&
                      configuringAddress !== item.address && (
                        <p className='text-sm text-danger break-all'>
                          {item.setup_error ===
                          'GOOGLE_GROUP_PROPAGATION_PENDING'
                            ? t(
                                'Google aún está preparando el grupo. Espera un momento y pulsa Reintentar configuración; no necesitas crear otra dirección.'
                              )
                            : item.setup_error}
                        </p>
                      )}
                    {item.verification_subject && (
                      <div className='text-sm mt-3 space-y-2'>
                        <p>
                          {t(
                            'Envía un correo a esta dirección con este asunto exacto; luego presiona Verificar recepción.'
                          )}
                        </p>
                        <code className='block break-all'>
                          {item.verification_subject}
                        </code>
                        <Button
                          size='sm'
                          variant='flat'
                          onPress={() => void copy(item.verification_subject!)}
                        >
                          {t('Copiar asunto')}
                        </Button>
                      </div>
                    )}
                    {item.can_dismiss && (
                      <Button
                        size='sm'
                        variant='light'
                        color='danger'
                        isDisabled={saving}
                        aria-label={`${t('Limpiar registro')}: ${item.address}`}
                        onPress={() => setCleanupTarget(item)}
                      >
                        {t('Limpiar registro')}
                      </Button>
                    )}
                  </div>
                  <div className='flex flex-wrap items-center gap-2 shrink-0'>
                    <Chip
                      size='sm'
                      variant='flat'
                      color={item.active ? 'success' : 'default'}
                    >
                      {configuringAddress === item.address ||
                      item.setup_status === 'provisioning'
                        ? t('Configurando')
                        : item.setup_status === 'pending_verification'
                          ? t('Pendiente de verificación')
                          : item.setup_status === 'error'
                            ? t('Error de configuración')
                            : item.active
                              ? t('Activa')
                              : t('Pausada')}
                    </Chip>
                    {item.setup_status !== 'ready' ? (
                      <Button
                        size='sm'
                        color='primary'
                        isDisabled={saving || !data.adminConfigured}
                        onPress={() =>
                          void setupAction(
                            item,
                            item.setup_status === 'pending_verification'
                          )
                        }
                      >
                        {item.setup_status === 'pending_verification'
                          ? t('Verificar recepción')
                          : t('Reintentar configuración')}
                      </Button>
                    ) : (
                      <>
                        <Button
                          size='sm'
                          variant='light'
                          aria-label={`${t('Copiar dirección')}: ${item.address}`}
                          onPress={() => void copy(item.address)}
                        >
                          {t('Copiar dirección')}
                        </Button>
                        <Button
                          size='sm'
                          variant='flat'
                          isDisabled={saving}
                          onPress={() => {
                            setToggleTarget(item);
                            setReactivateConfirmed(
                              item.provider === 'google_group'
                            );
                          }}
                        >
                          {item.active ? t('Pausar') : t('Reactivar')}
                        </Button>
                      </>
                    )}
                  </div>
                </CardBody>
              </Card>
            ))}
            {data.total > 25 && (
              <Pagination
                aria-label={t('Páginas')}
                page={page}
                total={Math.ceil(data.total / 25)}
                onChange={setPage}
              />
            )}
          </div>
        ) : (
          <EmptyState
            title={t('No hay direcciones registradas para esta selección.')}
          />
        )}
      </section>

      {exporterId && (
        <div className='border-t border-divider pt-5'>
          <p className='font-semibold mb-3'>
            {exporterName(Number(exporterId))}
          </p>
          <InboxSenderApprovals
            key={exporterId}
            exporterId={Number(exporterId)}
          />
        </div>
      )}
      <Modal
        isOpen={!!cleanupTarget}
        onClose={() => {
          if (!saving) setCleanupTarget(null);
        }}
        isDismissable={!saving}
        isKeyboardDismissDisabled={saving}
        hideCloseButton={saving}
      >
        <ModalContent>
          <ModalHeader>{t('Limpiar configuración incompleta')}</ModalHeader>
          <ModalBody>
            <p className='font-medium break-all'>{cleanupTarget?.address}</p>
            {cleanupTarget && <p>{exporterName(cleanupTarget.exporter_id)}</p>}
            <p>
              {t(
                'Se ocultará este intento de configuración. No se eliminarán grupos de Google, correos ni archivos.'
              )}
            </p>
            <p className='text-sm text-default-500'>
              {t(
                'El historial y la reserva de la dirección para esta exportadora se conservan. No se libera el nombre para otra exportadora.'
              )}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              isDisabled={saving}
              onPress={() => setCleanupTarget(null)}
            >
              {t('Cancelar')}
            </Button>
            <Button
              color='danger'
              isLoading={saving}
              onPress={() => void cleanup()}
            >
              {t('Limpiar registro')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={!!toggleTarget}
        onClose={() => {
          if (!saving) setToggleTarget(null);
        }}
        isDismissable={!saving}
        isKeyboardDismissDisabled={saving}
        hideCloseButton={saving}
      >
        <ModalContent>
          <ModalHeader>
            {toggleTarget?.active
              ? t('Pausar recepción')
              : t('Reactivar recepción')}
          </ModalHeader>
          <ModalBody>
            <p className='font-medium break-all'>{toggleTarget?.address}</p>
            {toggleTarget && <p>{exporterName(toggleTarget.exporter_id)}</p>}
            <p className='text-sm text-default-500'>
              {t(
                'Pausar detiene nuevas importaciones después del ciclo en curso. Los archivos existentes siguen disponibles. Al reactivar se pueden importar correos pendientes dentro de la ventana de sincronización.'
              )}
            </p>
            {!toggleTarget?.active &&
              toggleTarget?.provider !== 'google_group' && (
                <Checkbox
                  isSelected={reactivateConfirmed}
                  onValueChange={setReactivateConfirmed}
                  isDisabled={saving}
                >
                  {t(
                    'Confirmo que la ruta en Google está configurada y recibí el correo de prueba.'
                  )}
                </Checkbox>
              )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              isDisabled={saving}
              onPress={() => setToggleTarget(null)}
            >
              {t('Cancelar')}
            </Button>
            <Button
              color='primary'
              isLoading={saving}
              isDisabled={!toggleTarget?.active && !reactivateConfirmed}
              onPress={() => void toggle()}
            >
              {toggleTarget?.active ? t('Pausar') : t('Reactivar')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageLayout>
  );
}
